import React, { useState, useEffect, useRef } from 'react';
import { eventsAPI, compatAPI } from '../../utils/api';

// BaselinePage — side-by-side comparison of two app builds. Defaults to
// (left = current shipping build, right = previous build with ≥5
// sessions in the lookback window). Both selectable via the dropdowns
// at the top. Driven by the new GET
// /admin/api/events/baseline_compare endpoint, which:
//   - reads CURRENT_APP_VERSION_NAME from the env (falls back to the
//     value baked into release_filter.js),
//   - returns per-build KPIs (sessions / success_rate / error_rate /
//     error_count) plus the symmetric-difference of error_codes,
//   - flags each Δ as improved / regressed / same / na so this page can
//     paint regressions red without recomputing.
//
// Note on KPIs: ttfv_p95 and crash_free_rate come back as null today —
// the upstream events to populate them ship in the v1.11.20 cycle (see
// MEMORY.md "v1.11.20.5beta"). We simply skip rendering null KPIs.

const DAYS_OPTIONS = [
    { id: 7,   label: 'Last 7d' },
    { id: 14,  label: 'Last 14d' },
    { id: 30,  label: 'Last 30d' },
    { id: 90,  label: 'Last 90d' },
];

function fmtPct(v) {
    if (v == null) return '-';
    return (v * 100).toFixed(1) + '%';
}

function fmtNum(v) {
    if (v == null) return '-';
    return Number(v).toLocaleString();
}

function DeltaCell({ delta, fmt = fmtNum, suffix = '' }) {
    if (!delta || delta.flag === 'na') {
        return <td className="px-4 py-2 text-sm text-gray-400">—</td>;
    }
    const flag = delta.flag;
    const colour = flag === 'improved' ? 'text-green-700' : flag === 'regressed' ? 'text-red-700' : 'text-gray-500';
    const arrow  = flag === 'improved' ? 'up' : flag === 'regressed' ? 'down' : '=';
    const big    = delta.pct != null && Math.abs(delta.pct) > 20;  // brief: flag >20% as big regression
    return (
        <td className={`px-4 py-2 text-sm font-mono ${colour} ${big && flag === 'regressed' ? 'font-bold' : ''}`}>
            <span className="inline-block w-4">{arrow === 'up' ? '↑' : arrow === 'down' ? '↓' : '='}</span>
            {delta.abs != null ? fmt(delta.abs) + suffix : ''}
            {delta.pct != null && (
                <span className="ml-1 text-xs opacity-80">
                    ({delta.pct > 0 ? '+' : ''}{delta.pct.toFixed(1)}%)
                </span>
            )}
        </td>
    );
}

function KpiRow({ label, leftVal, rightVal, delta, fmt = fmtNum, suffix = '', hideIfBothNull = false }) {
    if (hideIfBothNull && leftVal == null && rightVal == null) return null;
    return (
        <tr className="hover:bg-gray-50">
            <td className="px-4 py-2 text-sm font-medium text-gray-700">{label}</td>
            <td className="px-4 py-2 text-sm font-mono text-gray-900">{leftVal != null ? fmt(leftVal) + suffix : '-'}</td>
            <td className="px-4 py-2 text-sm font-mono text-gray-900">{rightVal != null ? fmt(rightVal) + suffix : '-'}</td>
            <DeltaCell delta={delta} fmt={fmt} suffix={suffix} />
        </tr>
    );
}

export default function BaselinePage() {
    const [days, setDays] = useState(30);
    const [left, setLeft] = useState('');           // '' = use server default (current build)
    const [right, setRight] = useState('');         // '' = use server default (previous build)
    const [data, setData] = useState(null);
    const [appVersions, setAppVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch the candidate-builds list in parallel with the compare so
    // the dropdowns are populated even before the user picks anything.
    useEffect(() => {
        compatAPI.getBuildsSeen(days).then(r => {
            setAppVersions(r.data?.app_versions || []);
        }).catch(() => {});
    }, [days]);

    useEffect(() => {
        setLoading(true);
        const params = { days };
        if (left)  params.left  = left;
        if (right) params.right = right;
        eventsAPI.getBaselineCompare(params)
            .then(res => {
                setData(res.data);
                setError(null);
            })
            .catch(err => {
                console.error('baseline_compare error:', err);
                setError('Failed to load baseline data');
            })
            .finally(() => setLoading(false));
    }, [days, left, right]);

    const leftK    = data?.left;
    const rightK   = data?.right;
    const delta    = data?.delta || {};
    const errorsNew  = data?.errors_new_in_left  || [];
    const errorsGone = data?.errors_gone_in_left || [];
    const candidates = data?.candidates || appVersions;
    const currentVer = data?.current_app_version_name;

    return (
        <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Release Baseline</h2>
            <p className="text-sm text-gray-500 mb-4">
                Side-by-side comparison of two app builds.
                Defaults to current shipping build vs the previous build with ≥5 sessions in the window.
            </p>

            {/* Selector row */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-4 text-sm flex items-center gap-3 flex-wrap">
                <span className="text-xs uppercase text-gray-500">Compare</span>
                <select
                    value={left || (leftK?.app_version_name || '')}
                    onChange={e => setLeft(e.target.value)}
                    className="border rounded px-2 py-1">
                    <option value="">(current shipping{currentVer ? `: ${currentVer}` : ''})</option>
                    {candidates.map(c => (
                        <option key={c.app_version_name} value={c.app_version_name}>
                            {c.app_version_name} · {c.session_count || c.sessions || 0} sess
                        </option>
                    ))}
                </select>
                <span className="text-xs text-gray-500">vs</span>
                <select
                    value={right || (rightK?.app_version_name || '')}
                    onChange={e => setRight(e.target.value)}
                    className="border rounded px-2 py-1">
                    <option value="">(previous build)</option>
                    {candidates.map(c => (
                        <option key={c.app_version_name} value={c.app_version_name}>
                            {c.app_version_name} · {c.session_count || c.sessions || 0} sess
                        </option>
                    ))}
                </select>
                <span className="text-xs uppercase text-gray-500 ml-3">Window</span>
                <select
                    value={days}
                    onChange={e => setDays(Number(e.target.value))}
                    className="border rounded px-2 py-1">
                    {DAYS_OPTIONS.map(d => (
                        <option key={d.id} value={d.id}>{d.label}</option>
                    ))}
                </select>
            </div>

            {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded mb-4 text-sm text-red-700">
                    {error}
                </div>
            )}

            {loading && !data && (
                <div className="text-gray-400">Loading…</div>
            )}

            {data && (
                <>
                    {/* KPI table */}
                    <div className="bg-white rounded-lg shadow-md p-4 mb-4">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Metric</th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                            Left
                                            <div className="font-mono text-[10px] normal-case text-gray-400 mt-0.5">
                                                {leftK?.app_version_name || '?'}
                                            </div>
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                            Right
                                            <div className="font-mono text-[10px] normal-case text-gray-400 mt-0.5">
                                                {rightK?.app_version_name || '?'}
                                            </div>
                                        </th>
                                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Δ (left vs right)</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    <KpiRow
                                        label="Sessions"
                                        leftVal={leftK?.sessions}
                                        rightVal={rightK?.sessions}
                                        delta={delta.sessions} />
                                    <KpiRow
                                        label="Unique devices"
                                        leftVal={leftK?.unique_devices}
                                        rightVal={rightK?.unique_devices}
                                        delta={null} />
                                    <KpiRow
                                        label="Cast success rate"
                                        leftVal={leftK?.success_rate}
                                        rightVal={rightK?.success_rate}
                                        delta={delta.success_rate}
                                        fmt={fmtPct} />
                                    <KpiRow
                                        label="Error count"
                                        leftVal={leftK?.error_count}
                                        rightVal={rightK?.error_count}
                                        delta={delta.error_count} />
                                    <KpiRow
                                        label="Error rate"
                                        leftVal={leftK?.error_rate}
                                        rightVal={rightK?.error_rate}
                                        delta={delta.error_rate}
                                        fmt={fmtPct} />
                                    <KpiRow
                                        label="TTFV p95"
                                        leftVal={leftK?.ttfv_p95_ms}
                                        rightVal={rightK?.ttfv_p95_ms}
                                        delta={null}
                                        suffix=" ms"
                                        hideIfBothNull />
                                    <KpiRow
                                        label="Crash-free rate"
                                        leftVal={leftK?.crash_free_rate}
                                        rightVal={rightK?.crash_free_rate}
                                        delta={null}
                                        fmt={fmtPct}
                                        hideIfBothNull />
                                </tbody>
                            </table>
                        </div>
                        {(leftK?.ttfv_p95_ms == null && rightK?.ttfv_p95_ms == null &&
                          leftK?.crash_free_rate == null && rightK?.crash_free_rate == null) && (
                            <div className="text-xs text-gray-400 mt-2 px-4">
                                TTFV p95 and crash-free rate are skipped — those KPIs require
                                v1.11.20 events that are not yet collected. Will populate once data accumulates.
                            </div>
                        )}
                    </div>

                    {/* New errors only on the LEFT build */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white rounded-lg shadow-md p-4">
                            <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">
                                NEW errors only on LEFT
                                <span className="ml-2 normal-case text-xs text-gray-400">
                                    (errors that appear in {leftK?.app_version_name || 'left'} but not in {rightK?.app_version_name || 'right'})
                                </span>
                            </h3>
                            {errorsNew.length === 0 ? (
                                <div className="text-sm text-gray-400">No new errors.</div>
                            ) : (
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error code</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Occ.</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Devices</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {errorsNew.map((e, i) => (
                                            <tr key={i} className="hover:bg-red-50">
                                                <td className="px-4 py-2 text-sm font-mono text-red-700">{e.error_code}</td>
                                                <td className="px-4 py-2 text-sm font-semibold">{fmtNum(e.occurrences)}</td>
                                                <td className="px-4 py-2 text-sm">{fmtNum(e.unique_devices)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="bg-white rounded-lg shadow-md p-4">
                            <h3 className="text-sm font-bold text-gray-700 uppercase mb-2">
                                FIXED errors on LEFT
                                <span className="ml-2 normal-case text-xs text-gray-400">
                                    (errors that were on {rightK?.app_version_name || 'right'} but are gone from {leftK?.app_version_name || 'left'})
                                </span>
                            </h3>
                            {errorsGone.length === 0 ? (
                                <div className="text-sm text-gray-400">No fixed errors.</div>
                            ) : (
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error code</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Occ. on right</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {errorsGone.map((e, i) => (
                                            <tr key={i} className="hover:bg-green-50">
                                                <td className="px-4 py-2 text-sm font-mono text-green-700">{e.error_code}</td>
                                                <td className="px-4 py-2 text-sm font-semibold">{fmtNum(e.occurrences_in_right)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>

                    {/* Live connection errors on the current build */}
                    <ConnectionErrorsWidget />
                </>
            )}
        </div>
    );
}

// ConnectionErrorsWidget — auto-scoped to the current build, last 24h,
// across all telemetry layers. Polls every 10s. Pre-grouped by stage so
// the user sees "where in the pipeline things are failing right now"
// without having to pivot manually. Shares the same backend endpoint
// (/admin/api/compat/errors?group_by=stage) that powers the leaderboard.
function ConnectionErrorsWidget() {
    const [rows, setRows] = useState([]);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [loading, setLoading] = useState(true);
    const intervalRef = useRef(null);

    const fetchOnce = async () => {
        try {
            const res = await compatAPI.getErrors({
                group_by:    'stage',
                days:        1,
                release_op:  'current',
                layer:       'all',
            });
            const data = res.data?.data || [];
            setRows(data);
            setLastUpdated(new Date());
        } catch (err) {
            console.error('connection_errors fetch failed:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOnce();
        intervalRef.current = setInterval(fetchOnce, 10000);
        return () => intervalRef.current && clearInterval(intervalRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="bg-white rounded-lg shadow-md p-4 mt-4">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <h3 className="text-sm font-bold text-gray-700 uppercase">
                    Connection errors on this build (live)
                    <span className="ml-2 normal-case text-xs text-gray-400">
                        last 24h · current shipping · all layers · grouped by stage · polls every 10s
                    </span>
                </h3>
                <div className="text-xs text-gray-400">
                    {loading && rows.length === 0
                        ? 'loading…'
                        : lastUpdated
                            ? 'updated ' + lastUpdated.toLocaleTimeString()
                            : ''}
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stage</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Occ.</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Distinct codes</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Affected phones</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Top 3 codes</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {rows.map((r, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                                <td className="px-4 py-2 text-sm font-mono">{r.error_stage || '?'}</td>
                                <td className="px-4 py-2 text-sm font-semibold">{r.occurrences}</td>
                                <td className="px-4 py-2 text-sm">{r.distinct_codes}</td>
                                <td className="px-4 py-2 text-sm">{r.unique_devices ?? '-'}</td>
                                <td className="px-4 py-2">
                                    {(r.top_codes || []).slice(0, 3).map((c, j) => (
                                        <span key={j} className="inline-block mr-2 font-mono text-xs">
                                            {c.error_code}
                                            <span className="text-gray-400 ml-0.5">·{c.occurrences}</span>
                                        </span>
                                    ))}
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr><td colSpan="5" className="px-4 py-6 text-sm text-gray-400 text-center">
                                No connection errors on the current build in the last 24h.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
